/**
 * Custom domains for Sites (internal/sites-control-plane-api.md §6d).
 * Client-side mirror of castr `api/sites-domains.js` hostname rules so the form
 * can refuse obvious mistakes before the request; castr stays authoritative.
 */

export const HOSTNAME_MAX = 253
const LABEL_MAX = 63
const LABEL_PATTERN = /^[a-z0-9]([a-z0-9-]*[a-z0-9])?$/
const IPV4_PATTERN = /^\d{1,3}(\.\d{1,3}){3}$/

/** Lowercase, trimmed, no trailing dot, no scheme/path/port a user may have pasted. */
export function normalizeHostname(input) {
	let host = String(input ?? '').trim().toLowerCase()
	host = host.replace(/^[a-z][a-z0-9+.-]*:\/\//, '')
	host = host.split(/[/?#]/)[0]
	host = host.replace(/:\d+$/, '')
	return host.replace(/\.+$/, '')
}

/**
 * @param {string} hostname normalised
 * @param {{ baseDomain?: string, cnameTarget?: string, existing?: string[] }} [ctx]
 *   `baseDomain` is the site's Castr address zone (derived from `site.primaryHost`),
 *   `existing` the hostnames already attached to this site.
 * @return {{ valid: boolean, error?: string, message?: string }}
 */
export function validateHostname(hostname, ctx = {}) {
	if (typeof hostname !== 'string' || !hostname.length) {
		return { valid: false, error: 'required', message: 'Enter the domain you want to connect.' }
	}
	if (hostname.length > HOSTNAME_MAX) {
		return { valid: false, error: 'length', message: `A domain can be at most ${HOSTNAME_MAX} characters.` }
	}
	if (IPV4_PATTERN.test(hostname) || hostname.includes(':') || hostname.startsWith('[')) {
		return { valid: false, error: 'format', message: 'Enter a domain name, not an IP address.' }
	}
	if (hostname.includes('*')) {
		return { valid: false, error: 'format', message: 'Wildcard domains are not supported — add each hostname separately.' }
	}
	if (/[^a-z0-9.-]/.test(hostname)) {
		return { valid: false, error: 'format', message: 'Only letters, numbers, hyphens and dots are allowed.' }
	}
	const labels = hostname.split('.')
	if (labels.length < 2) {
		return { valid: false, error: 'format', message: 'Enter a full domain, for example watch.yourbrand.com.' }
	}
	if (labels.some((l) => !l.length || l.length > LABEL_MAX || !LABEL_PATTERN.test(l))) {
		return { valid: false, error: 'format', message: 'Each part must be 1–63 characters and cannot start or end with a hyphen.' }
	}
	const tld = labels[labels.length - 1]
	if (!/^[a-z][a-z0-9-]*$/.test(tld)) {
		return { valid: false, error: 'format', message: 'The domain must end in a valid top-level domain (for example .com).' }
	}
	const base = normalizeHostname(ctx.baseDomain)
	if (base && (hostname === base || hostname.endsWith(`.${base}`))) {
		return { valid: false, error: 'reserved', message: `Addresses under ${base} are assigned by Castr and cannot be added here.` }
	}
	const target = normalizeHostname(ctx.cnameTarget)
	if (target && hostname === target) {
		return { valid: false, error: 'reserved', message: 'That is the Castr CNAME target, not a domain you own.' }
	}
	if ((ctx.existing || []).some((h) => normalizeHostname(h) === hostname)) {
		return { valid: false, error: 'duplicate', message: 'This domain is already connected to this site.' }
	}
	return { valid: true }
}

/**
 * A bare/apex domain (`yourbrand.com`) versus a subdomain (`watch.yourbrand.com`).
 * Heuristic on label count: good enough to decide whether to show the
 * CNAME-at-apex caveat; the DNS provider is the real judge.
 */
export function isApexDomain(hostname) {
	const labels = normalizeHostname(hostname).split('.')
	if (labels.length <= 2) return true
	// second-level public suffixes (co.uk, com.au, …) keep the domain at 3 labels
	return labels.length === 3 && labels[1].length <= 3 && labels[labels.length - 1].length === 2
}

const SECOND_LEVEL_SUFFIXES = new Set(['co', 'com', 'net', 'org', 'gov', 'edu', 'ac', 'ne', 'or', 'go', 'gob', 'ltd', 'plc', 'nom', 'mil'])

/**
 * The registrable domain of a hostname (`watch.example.co.uk` → `example.co.uk`),
 * the only part of a custom domain analytics may carry. A short list of
 * second-level suffixes under two-letter TLDs, not the full public suffix list:
 * an unknown suffix yields a shorter domain, never an extra subdomain label.
 */
export function registrableDomain(hostname) {
	const labels = normalizeHostname(hostname).split('.').filter(Boolean)
	if (labels.length <= 2) return labels.join('.')
	const tld = labels[labels.length - 1]
	const sld = labels[labels.length - 2]
	const take = tld.length === 2 && SECOND_LEVEL_SUFFIXES.has(sld) ? 3 : 2
	return labels.slice(-take).join('.')
}

/** The zone the Castr addresses live under, from a site's `primaryHost` (`acme.sites.castr.io` → `sites.castr.io`). */
export function baseDomainOf(primaryHost) {
	const host = normalizeHostname(primaryHost)
	const dot = host.indexOf('.')
	return dot > 0 ? host.slice(dot + 1) : ''
}

/** `phase` from castr; falls back to `status` for older payloads. */
export function domainPhase(domain) {
	if (domain?.phase) return domain.phase
	if (domain?.status === 'active') return 'active'
	if (domain?.status === 'failed') return 'failed'
	return 'pending_dns'
}

export const DOMAIN_PHASE_LABELS = {
	pending_dns: 'Pending DNS',
	pending_ssl: 'Verifying certificate',
	active: 'Active',
	failed: 'Failed',
}

export const DOMAIN_PHASE_VARIANTS = {
	pending_dns: 'warning',
	pending_ssl: 'accent',
	active: 'success',
	failed: 'muted',
}

export function domainPhaseLabel(domain) {
	return DOMAIN_PHASE_LABELS[domainPhase(domain)] || 'Pending'
}

export function domainPhaseVariant(domain) {
	return DOMAIN_PHASE_VARIANTS[domainPhase(domain)] || 'muted'
}

/**
 * The routing provider's wording, trimmed to what a site owner can act on.
 * Cloudflare for SaaS sentences ("custom hostname does not CNAME to this zone")
 * and DigitalOcean App Platform step messages ("verify-cname: CNAME record for
 * … does not point to …", "ensure-certificate: …") both land here.
 */
export function humanizeDomainError(text) {
	const msg = String(text || '').trim()
	if (!msg) return ''
	if (/does not CNAME/i.test(msg)) return 'The CNAME record has not been found yet. DNS changes can take a few minutes to propagate.'
	if (/^verify-cname\b/i.test(msg) || /does not point to/i.test(msg)) {
		return 'The DNS record has not been found yet (or does not point at Castr). DNS changes can take a few minutes to propagate.'
	}
	if (/^verify-(nameservers|dns)\b/i.test(msg)) return 'The DNS records have not been found yet. DNS changes can take a few minutes to propagate.'
	if (/ownership/i.test(msg) && /txt/i.test(msg)) return 'The ownership TXT record has not been found yet.'
	if (/CAA/i.test(msg)) return 'A CAA record on your domain blocks certificate issuance. Allow letsencrypt.org, pki.goog and ssl.com, or remove the CAA record.'
	if (/rate limit/i.test(msg)) return 'The certificate authority rate-limited this domain. Try again later.'
	// DO step prefix (`ensure-certificate: …`) is for operators; the owner gets the sentence.
	const body = msg.replace(/^[a-z][a-z0-9-]*:\s+/i, '')
	if (/^ensure-certificate\b|^ensure-CA-authorization\b/i.test(msg)) return `The certificate could not be issued yet: ${body.replace(/\.$/, '')}.`
	return body.replace(/\.$/, '') + '.'
}

export const RECORD_PURPOSE_LABELS = {
	routing: 'Points your domain at Castr (required)',
	ownership: 'Proves you own the domain (optional — the CNAME already does this once it resolves)',
	certificate: 'Certificate validation (only if the CNAME cannot be used)',
}
